import { IDropdownOption, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  Dropdown,
  Loader,
  loaderStylesSpinnerXLarge,
  TextField,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useEffect, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  ListableFeatureSetToPermission,
  ListableProjectToPermission,
  ListFeatureSetsPermissionsPageResponse,
  ListProjectsPermissionsPageResponse,
  PermissionState,
  PermissionType,
  SubmitPendingPermissionRequest,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { permissionTypeNameMap } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import {
  IRequestPermissionClassNames,
  IRequestPermissionStyles,
  requestPermissionStylesDefault,
} from './RequestPermission.styles';

const getPermissionTypeOptions = (pendingTypeSet: Set<PermissionType | undefined>) => {
  const options: IDropdownOption[] = [];
  permissionTypeNameMap.forEach((text, key) => {
    if (!pendingTypeSet.has(key)) {
      options.push({ key, text });
    }
  });
  return options;
};

interface IRequestPermissionProps {
  projectId?: string;
  featureSetId?: string;
  onSave?: () => void;
  onCancel?: () => void;
}

export const RequestPermission = ({ projectId, featureSetId, onSave, onCancel }: IRequestPermissionProps) => {
  const [values, setValues] = useState<SubmitPendingPermissionRequest>({
    resourceId: projectId || featureSetId,
  });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [permissionTypeOptions, setPermissionTypeOptions] = useState<IDropdownOption[]>([]);
  const { addToast } = useToast();
  const load = useCallback(async () => {
    setLoadingMessage(`Loading Access Type List`);
    try {
      const api = projectId ? CoreService.ListProjectPermissionsPage : CoreService.ListFeatureSetsPermissionsPage;
      const { entries = [] }: ListProjectsPermissionsPageResponse | ListFeatureSetsPermissionsPageResponse =
        await fsApi(api, {
          resourceId: projectId || featureSetId,
          filters: [PermissionState.PENDING],
          pageSize: 1000,
        });
      const pendingPermissionTypeSet = new Set<PermissionType | undefined>();
      entries.forEach((d: ListableProjectToPermission | ListableFeatureSetToPermission) => {
        pendingPermissionTypeSet.add(d.permission?.permission);
      });
      setPermissionTypeOptions(getPermissionTypeOptions(pendingPermissionTypeSet));
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while loading Access Type List: `));
    } finally {
      setLoadingMessage('');
    }
  }, [addToast, projectId, featureSetId]);
  const onUpdate = useCallback(async (updatedValues: SubmitPendingPermissionRequest) => {
    try {
      const api = projectId
        ? CoreService.SubmitPendingProjectPermission
        : CoreService.SubmitPendingFeatureSetPermission;
      await fsApi(api, {
        resourceId: updatedValues.resourceId,
        permission: updatedValues.permission,
        reason: updatedValues.reason,
      });
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Access Requested Successfully!',
      });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while requesting access: `));
    } finally {
      onSave && onSave();
    }
  }, []);
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      onUpdate(values);
      e.preventDefault();
    },
    [values]
  );
  useEffect(() => {
    load();
  }, [load]);
  const classNames = useClassNames<IRequestPermissionStyles, IRequestPermissionClassNames>(
    'RequestPermission',
    requestPermissionStylesDefault
  );
  return (
    <div className={classNames.root} data-test="request-permission">
      <form className={classNames.form} onSubmit={onSubmit}>
        <div className={classNames.fields}>
          <Dropdown
            label="Access Type"
            placeholder="Select Access Type"
            options={permissionTypeOptions}
            onChange={(_e, option) => {
              if (option) {
                setValues({ ...values, permission: option.key as PermissionType });
              }
            }}
          />
          <TextField
            name="reason"
            label="Business Reason"
            multiline
            rows={3}
            onChange={(_e, reason) => {
              if (reason !== undefined) setValues({ ...values, reason });
            }}
          />
        </div>
        <div className={classNames.buttons}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} style={{ marginRight: 8 }} />
          <Button styles={buttonStylesPrimary} type="submit" text="Request Access" disabled={!values.permission} />
        </div>
      </form>
      {loadingMessage && <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />}
    </div>
  );
};
